
import React, { useState, useMemo } from "react";
import { ChevronDown, Flag, Phone } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// Comprehensive list of country codes with flags
const countryCodes = [
  { code: "1", name: "United States", flag: "🇺🇸" },
  { code: "1", name: "Canada", flag: "🇨🇦" },
  { code: "1242", name: "Bahamas", flag: "🇧🇸" },
  { code: "1246", name: "Barbados", flag: "🇧🇧" },
  { code: "1264", name: "Anguilla", flag: "🇦🇮" },
  { code: "1268", name: "Antigua and Barbuda", flag: "🇦🇬" },
  { code: "1284", name: "British Virgin Islands", flag: "🇻🇬" },
  { code: "1340", name: "U.S. Virgin Islands", flag: "🇻🇮" },
  { code: "1345", name: "Cayman Islands", flag: "🇰🇾" },
  { code: "1441", name: "Bermuda", flag: "🇧🇲" },
  { code: "1473", name: "Grenada", flag: "🇬🇩" },
  { code: "1649", name: "Turks and Caicos Islands", flag: "🇹🇨" },
  { code: "1664", name: "Montserrat", flag: "🇲🇸" },
  { code: "1671", name: "Guam", flag: "🇬🇺" },
  { code: "1721", name: "Sint Maarten", flag: "🇸🇽" },
  { code: "1758", name: "Saint Lucia", flag: "🇱🇨" },
  { code: "1767", name: "Dominica", flag: "🇩🇲" },
  { code: "1784", name: "Saint Vincent and the Grenadines", flag: "🇻🇨" },
  { code: "1787", name: "Puerto Rico", flag: "🇵🇷" },
  { code: "1809", name: "Dominican Republic", flag: "🇩🇴" },
  { code: "1868", name: "Trinidad and Tobago", flag: "🇹🇹" },
  { code: "1869", name: "Saint Kitts and Nevis", flag: "🇰🇳" },
  { code: "1876", name: "Jamaica", flag: "🇯🇲" },
  { code: "20", name: "Egypt", flag: "🇪🇬" },
  { code: "211", name: "South Sudan", flag: "🇸🇸" },
  { code: "212", name: "Morocco", flag: "🇲🇦" },
  { code: "213", name: "Algeria", flag: "🇩🇿" },
  { code: "216", name: "Tunisia", flag: "🇹🇳" },
  { code: "218", name: "Libya", flag: "🇱🇾" },
  { code: "220", name: "Gambia", flag: "🇬🇲" },
  { code: "221", name: "Senegal", flag: "🇸🇳" },
  { code: "222", name: "Mauritania", flag: "🇲🇷" },
  { code: "223", name: "Mali", flag: "🇲🇱" },
  { code: "224", name: "Guinea", flag: "🇬🇳" },
  { code: "225", name: "Côte d'Ivoire", flag: "🇨🇮" },
  { code: "226", name: "Burkina Faso", flag: "🇧🇫" },
  { code: "227", name: "Niger", flag: "🇳🇪" },
  { code: "228", name: "Togo", flag: "🇹🇬" },
  { code: "229", name: "Benin", flag: "🇧🇯" },
  { code: "230", name: "Mauritius", flag: "🇲🇺" },
  { code: "231", name: "Liberia", flag: "🇱🇷" },
  { code: "232", name: "Sierra Leone", flag: "🇸🇱" },
  { code: "233", name: "Ghana", flag: "🇬🇭" },
  { code: "234", name: "Nigeria", flag: "🇳🇬" },
  { code: "235", name: "Chad", flag: "🇹🇩" },
  { code: "236", name: "Central African Republic", flag: "🇨🇫" },
  { code: "237", name: "Cameroon", flag: "🇨🇲" },
  { code: "238", name: "Cape Verde", flag: "🇨🇻" },
  { code: "239", name: "São Tomé and Príncipe", flag: "🇸🇹" },
  { code: "240", name: "Equatorial Guinea", flag: "🇬🇶" },
  { code: "241", name: "Gabon", flag: "🇬🇦" },
  { code: "242", name: "Republic of the Congo", flag: "🇨🇬" },
  { code: "243", name: "Democratic Republic of the Congo", flag: "🇨🇩" },
  { code: "244", name: "Angola", flag: "🇦🇴" },
  { code: "245", name: "Guinea-Bissau", flag: "🇬🇼" },
  { code: "246", name: "British Indian Ocean Territory", flag: "🇮🇴" },
  { code: "248", name: "Seychelles", flag: "🇸🇨" },
  { code: "249", name: "Sudan", flag: "🇸🇩" },
  { code: "250", name: "Rwanda", flag: "🇷🇼" },
  { code: "251", name: "Ethiopia", flag: "🇪🇹" },
  { code: "252", name: "Somalia", flag: "🇸🇴" },
  { code: "253", name: "Djibouti", flag: "🇩🇯" },
  { code: "254", name: "Kenya", flag: "🇰🇪" },
  { code: "255", name: "Tanzania", flag: "🇹🇿" },
  { code: "256", name: "Uganda", flag: "🇺🇬" },
  { code: "257", name: "Burundi", flag: "🇧🇮" },
  { code: "258", name: "Mozambique", flag: "🇲🇿" },
  { code: "260", name: "Zambia", flag: "🇿🇲" },
  { code: "261", name: "Madagascar", flag: "🇲🇬" },
  { code: "262", name: "Réunion", flag: "🇷🇪" },
  { code: "263", name: "Zimbabwe", flag: "🇿🇼" },
  { code: "264", name: "Namibia", flag: "🇳🇦" },
  { code: "265", name: "Malawi", flag: "🇲🇼" },
  { code: "266", name: "Lesotho", flag: "🇱🇸" },
  { code: "267", name: "Botswana", flag: "🇧🇼" },
  { code: "268", name: "Eswatini", flag: "🇸🇿" },
  { code: "269", name: "Comoros", flag: "🇰🇲" },
  { code: "27", name: "South Africa", flag: "🇿🇦" },
  { code: "291", name: "Eritrea", flag: "🇪🇷" },
  { code: "297", name: "Aruba", flag: "🇦🇼" },
  { code: "298", name: "Faroe Islands", flag: "🇫🇴" },
  { code: "299", name: "Greenland", flag: "🇬🇱" },
  { code: "30", name: "Greece", flag: "🇬🇷" },
  { code: "31", name: "Netherlands", flag: "🇳🇱" },
  { code: "32", name: "Belgium", flag: "🇧🇪" },
  { code: "33", name: "France", flag: "🇫🇷" },
  { code: "34", name: "Spain", flag: "🇪🇸" },
  { code: "350", name: "Gibraltar", flag: "🇬🇮" },
  { code: "351", name: "Portugal", flag: "🇵🇹" },
  { code: "352", name: "Luxembourg", flag: "🇱🇺" },
  { code: "353", name: "Ireland", flag: "🇮🇪" },
  { code: "354", name: "Iceland", flag: "🇮🇸" },
  { code: "355", name: "Albania", flag: "🇦🇱" },
  { code: "356", name: "Malta", flag: "🇲🇹" },
  { code: "357", name: "Cyprus", flag: "🇨🇾" },
  { code: "358", name: "Finland", flag: "🇫🇮" },
  { code: "359", name: "Bulgaria", flag: "🇧🇬" },
  { code: "36", name: "Hungary", flag: "🇭🇺" },
  { code: "370", name: "Lithuania", flag: "🇱🇹" },
  { code: "371", name: "Latvia", flag: "🇱🇻" },
  { code: "372", name: "Estonia", flag: "🇪🇪" },
  { code: "373", name: "Moldova", flag: "🇲🇩" },
  { code: "374", name: "Armenia", flag: "🇦🇲" },
  { code: "375", name: "Belarus", flag: "🇧🇾" },
  { code: "376", name: "Andorra", flag: "🇦🇩" },
  { code: "377", name: "Monaco", flag: "🇲🇨" },
  { code: "378", name: "San Marino", flag: "🇸🇲" },
  { code: "379", name: "Vatican City", flag: "🇻🇦" },
  { code: "380", name: "Ukraine", flag: "🇺🇦" },
  { code: "381", name: "Serbia", flag: "🇷🇸" },
  { code: "382", name: "Montenegro", flag: "🇲🇪" },
  { code: "383", name: "Kosovo", flag: "🇽🇰" },
  { code: "385", name: "Croatia", flag: "🇭🇷" },
  { code: "386", name: "Slovenia", flag: "🇸🇮" },
  { code: "387", name: "Bosnia and Herzegovina", flag: "🇧🇦" },
  { code: "389", name: "North Macedonia", flag: "🇲🇰" },
  { code: "39", name: "Italy", flag: "🇮🇹" },
  { code: "40", name: "Romania", flag: "🇷🇴" },
  { code: "41", name: "Switzerland", flag: "🇨🇭" },
  { code: "420", name: "Czech Republic", flag: "🇨🇿" },
  { code: "421", name: "Slovakia", flag: "🇸🇰" },
  { code: "423", name: "Liechtenstein", flag: "🇱🇮" },
  { code: "43", name: "Austria", flag: "🇦🇹" },
  { code: "44", name: "United Kingdom", flag: "🇬🇧" },
  { code: "45", name: "Denmark", flag: "🇩🇰" },
  { code: "46", name: "Sweden", flag: "🇸🇪" },
  { code: "47", name: "Norway", flag: "🇳🇴" },
  { code: "48", name: "Poland", flag: "🇵🇱" },
  { code: "49", name: "Germany", flag: "🇩🇪" },
  { code: "500", name: "Falkland Islands", flag: "🇫🇰" },
  { code: "501", name: "Belize", flag: "🇧🇿" },
  { code: "502", name: "Guatemala", flag: "🇬🇹" },
  { code: "503", name: "El Salvador", flag: "🇸🇻" },
  { code: "504", name: "Honduras", flag: "🇭🇳" },
  { code: "505", name: "Nicaragua", flag: "🇳🇮" },
  { code: "506", name: "Costa Rica", flag: "🇨🇷" },
  { code: "507", name: "Panama", flag: "🇵🇦" },
  { code: "508", name: "Saint Pierre and Miquelon", flag: "🇵🇲" },
  { code: "509", name: "Haiti", flag: "🇭🇹" },
  { code: "51", name: "Peru", flag: "🇵🇪" },
  { code: "52", name: "Mexico", flag: "🇲🇽" },
  { code: "53", name: "Cuba", flag: "🇨🇺" },
  { code: "54", name: "Argentina", flag: "🇦🇷" },
  { code: "55", name: "Brazil", flag: "🇧🇷" },
  { code: "56", name: "Chile", flag: "🇨🇱" },
  { code: "57", name: "Colombia", flag: "🇨🇴" },
  { code: "58", name: "Venezuela", flag: "🇻🇪" },
  { code: "590", name: "Guadeloupe", flag: "🇬🇵" },
  { code: "591", name: "Bolivia", flag: "🇧🇴" },
  { code: "592", name: "Guyana", flag: "🇬🇾" },
  { code: "593", name: "Ecuador", flag: "🇪🇨" },
  { code: "594", name: "French Guiana", flag: "🇬🇫" },
  { code: "595", name: "Paraguay", flag: "🇵🇾" },
  { code: "596", name: "Martinique", flag: "🇲🇶" },
  { code: "597", name: "Suriname", flag: "🇸🇷" },
  { code: "598", name: "Uruguay", flag: "🇺🇾" },
  { code: "599", name: "Curaçao", flag: "🇨🇼" },
  { code: "60", name: "Malaysia", flag: "🇲🇾" },
  { code: "61", name: "Australia", flag: "🇦🇺" },
  { code: "62", name: "Indonesia", flag: "🇮🇩" },
  { code: "63", name: "Philippines", flag: "🇵🇭" },
  { code: "64", name: "New Zealand", flag: "🇳🇿" },
  { code: "65", name: "Singapore", flag: "🇸🇬" },
  { code: "66", name: "Thailand", flag: "🇹🇭" },
  { code: "670", name: "Timor-Leste", flag: "🇹🇱" },
  { code: "672", name: "Norfolk Island", flag: "🇳🇫" },
  { code: "673", name: "Brunei", flag: "🇧🇳" },
  { code: "674", name: "Nauru", flag: "🇳🇷" },
  { code: "675", name: "Papua New Guinea", flag: "🇵🇬" },
  { code: "676", name: "Tonga", flag: "🇹🇴" },
  { code: "677", name: "Solomon Islands", flag: "🇸🇧" },
  { code: "678", name: "Vanuatu", flag: "🇻🇺" },
  { code: "679", name: "Fiji", flag: "🇫🇯" },
  { code: "680", name: "Palau", flag: "🇵🇼" },
  { code: "681", name: "Wallis and Futuna", flag: "🇼🇫" },
  { code: "682", name: "Cook Islands", flag: "🇨🇰" },
  { code: "683", name: "Niue", flag: "🇳🇺" },
  { code: "685", name: "Samoa", flag: "🇼🇸" },
  { code: "686", name: "Kiribati", flag: "🇰🇮" },
  { code: "687", name: "New Caledonia", flag: "🇳🇨" },
  { code: "688", name: "Tuvalu", flag: "🇹🇻" },
  { code: "689", name: "French Polynesia", flag: "🇵🇫" },
  { code: "690", name: "Tokelau", flag: "🇹🇰" },
  { code: "691", name: "Micronesia", flag: "🇫🇲" },
  { code: "692", name: "Marshall Islands", flag: "🇲🇭" },
  { code: "7", name: "Russia", flag: "🇷🇺" },
  { code: "7", name: "Kazakhstan", flag: "🇰🇿" },
  { code: "81", name: "Japan", flag: "🇯🇵" },
  { code: "82", name: "South Korea", flag: "🇰🇷" },
  { code: "84", name: "Vietnam", flag: "🇻🇳" },
  { code: "850", name: "North Korea", flag: "🇰🇵" },
  { code: "852", name: "Hong Kong", flag: "🇭🇰" },
  { code: "853", name: "Macau", flag: "🇲🇴" },
  { code: "855", name: "Cambodia", flag: "🇰🇭" },
  { code: "856", name: "Laos", flag: "🇱🇦" },
  { code: "86", name: "China", flag: "🇨🇳" },
  { code: "880", name: "Bangladesh", flag: "🇧🇩" },
  { code: "886", name: "Taiwan", flag: "🇹🇼" },
  { code: "90", name: "Turkey", flag: "🇹🇷" },
  { code: "91", name: "India", flag: "🇮🇳" },
  { code: "92", name: "Pakistan", flag: "🇵🇰" },
  { code: "93", name: "Afghanistan", flag: "🇦🇫" },
  { code: "94", name: "Sri Lanka", flag: "🇱🇰" },
  { code: "95", name: "Myanmar", flag: "🇲🇲" },
  { code: "960", name: "Maldives", flag: "🇲🇻" },
  { code: "961", name: "Lebanon", flag: "🇱🇧" },
  { code: "962", name: "Jordan", flag: "🇯🇴" },
  { code: "963", name: "Syria", flag: "🇸🇾" },
  { code: "964", name: "Iraq", flag: "🇮🇶" },
  { code: "965", name: "Kuwait", flag: "🇰🇼" },
  { code: "966", name: "Saudi Arabia", flag: "🇸🇦" },
  { code: "967", name: "Yemen", flag: "🇾🇪" },
  { code: "968", name: "Oman", flag: "🇴🇲" },
  { code: "970", name: "Palestine", flag: "🇵🇸" },
  { code: "971", name: "United Arab Emirates", flag: "🇦🇪" },
  { code: "972", name: "Israel", flag: "🇮🇱" },
  { code: "973", name: "Bahrain", flag: "🇧🇭" },
  { code: "974", name: "Qatar", flag: "🇶🇦" },
  { code: "975", name: "Bhutan", flag: "🇧🇹" },
  { code: "976", name: "Mongolia", flag: "🇲🇳" },
  { code: "977", name: "Nepal", flag: "🇳🇵" },
  { code: "992", name: "Tajikistan", flag: "🇹🇯" },
  { code: "993", name: "Turkmenistan", flag: "🇹🇲" },
  { code: "994", name: "Azerbaijan", flag: "🇦🇿" },
  { code: "995", name: "Georgia", flag: "🇬🇪" },
  { code: "996", name: "Kyrgyzstan", flag: "🇰🇬" },
  { code: "998", name: "Uzbekistan", flag: "🇺🇿" }
];

// Sort all countries alphabetically by name
const sortedCountries = [...countryCodes].sort((a, b) => 
  a.name.localeCompare(b.name)
);

// Move US to the top, keep all others (including Canada) in alphabetical order
const orderedCountries = [
  sortedCountries.find(c => c.name === "United States")!,
  ...sortedCountries.filter(c => c.name !== "United States")
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  countryCode: string;
  onCountryCodeChange: (code: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  onSubmit?: () => void;
}

export const PhoneInput = ({
  value,
  onChange,
  countryCode,
  onCountryCodeChange,
  placeholder = "Enter your phone number",
  className,
  disabled = false,
  onSubmit
}: PhoneInputProps) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && onSubmit) {
      e.preventDefault();
      onSubmit();
    }
  };

  // Find the current country
  const currentCountry = useMemo(() => 
    orderedCountries.find(c => c.code === countryCode) || orderedCountries[0], 
    [countryCode]
  );

  return (
    <div className={cn("flex w-full", className)}>
      <div className="relative">
        <Select
          value={countryCode}
          onValueChange={onCountryCodeChange}
          disabled={disabled}
        >
          <SelectTrigger 
            className="w-[85px] rounded-l-full px-3 bg-white text-black border-0 focus:ring-0 focus:ring-offset-0"
          >
            <SelectValue>
              <div className="flex items-center">
                <span className="mr-1">{currentCountry.flag}</span>
                <span>+{countryCode}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {orderedCountries.map((country) => (
              <SelectItem 
                key={`${country.code}-${country.name}`} 
                value={country.code}
                className="cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span className="whitespace-nowrap">
                    +{country.code} {country.name}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="relative flex-1">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <Phone className="h-4 w-4 text-muted-foreground" />
        </div>
        <Input
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="pl-10 rounded-r-full bg-white text-black border-0 placeholder:text-gray-500 focus:ring-0 focus:ring-offset-0"
          disabled={disabled}
          onKeyDown={handleKeyDown}
          aria-label="Phone number"
        />
      </div>
    </div>
  );
};
